import * as React from 'react';
import { CoughAnalysisSet } from 'webportalclient';
import { Recording } from "@vitalograph/coughmarshalclient";
import { createRange } from '../../../misc/math-functions';
import { ImportEvent, ImportStatus } from '../ImportEventsReducer';
import { ErrorBoundary } from '../../../errors/ErrorBoundary';
import { useCoughMarshalApiClient } from '../../../misc/api/CoughMarshalApiClient';

interface FileSelectionPageProps {
    hourlySegmentSelected: number;
    eventsFileSelected: string;
    analysisSet: CoughAnalysisSet;

    handleSetHourlySegment: (hs: number) => void;
    handleSetEventsFile: (filename: string) => void;
    handleEventsDataChange: (eventDataList: Array<ImportEvent>) => void;
    handleErrorMessage: (message: string) => void;
}

declare const coughMarshalRootUrl: string;

export const FileSelectionPage = (props: FileSelectionPageProps) => {
    const coughMarshalClient = useCoughMarshalApiClient();
    const [recording, setRecording] = React.useState<Recording>();

    React.useEffect(() => {
        const getSession = async () => {
            try {
                const recordingResult = await coughMarshalClient.getRecording(props.analysisSet.sessionId);
                setRecording(recordingResult);
            } catch {
                props.handleErrorMessage("Cannot retrieve cough recording from Cough Playback service");
            }
        }

        getSession();
    }, [props.analysisSet.sessionId]);

    const processFileContent = (text: string) => {
        let eventData = new Array<ImportEvent>();
        let lines = text.split(/\r\n|\r|\n/g);  // Split on line breaks

        for (var i = 0; i < lines.length; i++) {
            let records = lines[i].split('\t'); // Split on tabs

            if (records.length !== 2) continue;

            if (isNaN(Number(records[0]))) continue;

            eventData.push({ Id: i, Position: Number(records[0]), IsImporting: ImportStatus.NotImported });
        }

        if (eventData.length === 0) {
            props.handleErrorMessage('There are no events in the selected file');
            return;
        }

        if (eventData.length > 1000) {
            props.handleErrorMessage('A maximum of 1000 events can be imported at one time');
            return;
        }

        props.handleEventsDataChange(eventData);
    }

    const eventsFileloaded = (event: ProgressEvent<FileReader>) => {
        processFileContent(event.target.result as string);
    }

    const readEventsFile = (file: Blob) => {
        let reader = new FileReader();
        reader.onload = eventsFileloaded;

        reader.readAsText(file);
    }

    const handleFileChange = (files: FileList) => {
        props.handleErrorMessage(null);    // Reset any previous error messages

        let fileNameSubStrings = files[0].name.split('_');

        if (files[0].type !== 'text/plain') {
            props.handleErrorMessage('The file selected should be a text file (.txt)');
            return;
        }

        let fileHourIndicator = +fileNameSubStrings[0];

        if (isNaN(fileHourIndicator)) {
            props.handleSetHourlySegment(null);
        }

        if (fileHourIndicator > 0 && fileHourIndicator < 24) {
            props.handleSetHourlySegment(fileHourIndicator);
        }

        props.handleSetEventsFile(files[0].name);

        readEventsFile(files[0]);
    }

    const renderHourOption = () => {
        if (props.analysisSet && recording) {
            const TimeConstant = 60;
            let maxExpectedHour = Math.ceil(recording.rawDuration / TimeConstant / TimeConstant);

            let elements = createRange(1, maxExpectedHour, 1).map((i) => {
                return (<option id={(i-1).toString()} key={i}>{i}</option>);
            });

            return <div>
                    <select
                    defaultValue={props.hourlySegmentSelected ? props.hourlySegmentSelected.toString() : null}
                    onChange={(e) => props.handleSetHourlySegment(parseInt(e.target.value))}>
                    <option key={0}>Select a value</option>
                    {elements}
                    </select>
                </div>;
        } else {
            return null;
        }
    }

    const renderHourlySegmentIndicator = () => {
        if (props.hourlySegmentSelected || props.eventsFileSelected) {
            return (
                <div className="form-group">
                    <label>Hour Segment to import into</label> 
                    
                    {renderHourOption()}
                </div>
            );
        } else {
            return null;
        }
    }

    return (
        <ErrorBoundary>
            <div>
                <div className="row">
                    <div className="col-md-12">
                        <h2 className="text-primary">Select Events File for Import</h2>
                    </div>
                    <div className="col-md-12">
                        <div className="form-group">
                            <label>Select a cough events file to start importing events into the <i>Raw</i> waveform</label>
                            <input id="eventsFileUpload" className="btn btn-primary" type="file" accept=".txt" onChange={(e) => handleFileChange(e.target.files)} />
                        </div>
                    </div>
                </div>
                <div className="row">
                    <div className="col-md-12">
                        {renderHourlySegmentIndicator()}
                    </div>
                </div>
            </div>
        </ErrorBoundary>
    );
}
